import useGetAccountSettings from './hooks/useGetAccountSettings';
import NotificationPreferences from './NotificationPreferences.jsx';
import ChangeNumber from './ChangeNumber.jsx';
import ChangePassword from './ChangePassword.jsx';
import ChangeEmail from './ChangeEmail.jsx';
import InviteDependent from './InviteDependent.jsx';
import SubNav from '../../shared/SubNav';
import { Heading, Tabs, Box, Flex, Container } from '@radix-ui/themes';
import styles from './AccountSettings.module.css';

import { HashRouter } from 'react-router-dom';
import { useURLParam } from '../../lib/hooks/useURLParam';
import { useSelector } from 'react-redux';
import { isAdmin } from '../../lib/utils/roles';
import AdminNotificationPreferences from './AdminNotificationPreferences.jsx';

const TABS = {
  PREFERENCES: 'preferences',
  NOTIFICATIONS: 'notifications',
  PASSWORD: 'password',
  INVITE_DEPENDENT: 'invite-dependent',
};

function Settings() {
  const {
    pendingEmail,
    verificationNeeded,
    newPhoneNumber,
    canChangePassword,
    canInviteDependents,
  } = useGetAccountSettings();

  const { userType } = useSelector(state => state.currentUser);

  const navItems = [
    { id: TABS.PREFERENCES, label: 'Contact Preferences' },
    ...(isAdmin(userType)
      ? [{ id: TABS.NOTIFICATIONS, label: 'Admin Notifications' }]
      : []),
    ...(canChangePassword ? [{ id: TABS.PASSWORD, label: 'Password' }] : []),
    ...(canInviteDependents
      ? [{ id: TABS.INVITE_DEPENDENT, label: 'Invite Dependent' }]
      : []),
  ];

  const allowedTabs = navItems.map(item => item.id);

  const [getTabFromURL, setTabInURL] = useURLParam('tab', allowedTabs);
  const activeTab = getTabFromURL() || TABS.PREFERENCES;

  // Update URL when tab changes
  const handleTabChange = newTab => {
    setTabInURL(newTab);
  };

  return (
    <Container maxWidth="1400px">
      <Heading size="6" ml="6">
        Your Settings
      </Heading>
      <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
        <Flex className={styles.mainContainer}>
          <SubNav
            navItems={navItems}
            currentValue={activeTab}
            onValueChange={handleTabChange}
          />
          <Box className={styles.contentContainer}>
            <Tabs.Content value={TABS.PREFERENCES}>
              <NotificationPreferences />
              <Flex mt="4" gap="2" className={styles.mobileStack}>
                <Box style={{ flex: 1 }}>
                  <ChangeEmail pendingEmail={pendingEmail} />
                </Box>
                <Box style={{ flex: 1 }}>
                  <ChangeNumber
                    verificationNeeded={verificationNeeded}
                    newPhoneNumber={newPhoneNumber}
                  />
                </Box>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value={TABS.NOTIFICATIONS}>
              <Flex>
                <Box style={{ flex: 1 }}>
                  <AdminNotificationPreferences />
                </Box>
              </Flex>
            </Tabs.Content>
            <Tabs.Content value={TABS.PASSWORD}>
              {canChangePassword && <ChangePassword />}
            </Tabs.Content>
            <Tabs.Content value={TABS.INVITE_DEPENDENT}>
              {canInviteDependents && <InviteDependent />}
            </Tabs.Content>
          </Box>
        </Flex>
      </Tabs.Root>
    </Container>
  );
}

export default function AccountSettings() {
  return (
    <HashRouter hashType="noslash">
      <Settings />
    </HashRouter>
  );
}
